import React from "react";
import type { Localizer } from "@/main/react/Types";

/**
 * The "What do you think now?" composer — the sentence that IS the feature.
 *
 * <p><b>The mark is not the feature, the sentence is.</b> Reddit's delta is a symbol a bot parses into
 * a number on a leaderboard; here, marking a thought asks the post author what they think now and that
 * answer posts as a real thought. It is the most honest thing anyone writes on a debate site, it is a
 * comment and therefore activation, and it costs about eight seconds at the exact moment the author is
 * most willing to spend them. If the mark ever becomes possible without this, the feature has been
 * removed and replaced with a worse one.
 *
 * <p><b>Deliberately diverges from {@code BoardWhyComposer}, which it is otherwise modelled on: this
 * one is REQUIRED and that one is explicitly OPTIONAL.</b> Do not copy the "this is optional" note
 * across. A board asks for reasoning after a one-tap vote, where an obligation would cost more votes
 * than it gains comments; here the sentence is the whole act, and a mark without it is a points
 * system. The two components look alike and mean opposite things.
 *
 * <p>Flat-X per rule 23: a bordered panel rather than a shadowed card, pill controls, one focus ring.
 *
 * <p>Design: {@code designs/01-now/2026-08-08-three-participation-mechanics-ux-design.md} §4;
 * record: {@code designs/01-now/2026-08-08-changed-my-mind-implementation-record.md}.
 */
export function MindChangedComposer(props: {
    localizer: Localizer;
    submitting?: boolean;
    onSubmit: (text: string) => void;
    onCancel: () => void;
}) {
    const { localizer, submitting, onSubmit, onCancel } = props;
    const [text, setText] = React.useState("");
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

    // Focus on open. A composer that appears without focus is a thing on the page rather than an
    // invitation; on mobile it is also what raises the keyboard, which is the actual call to act.
    React.useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    const trimmed = text.trim();
    const canSubmit = trimmed.length > 0 && !submitting;

    return (
        <div
            className="mt-3 rounded-xl border border-border/70 bg-card p-3"
            data-playwright-id="discussion-mind-changed-composer"
        >
            <p className="text-sm font-medium text-foreground">
                {localizer("discussion.mind_changed.prompt")}
            </p>
            {/* Says what happens next, not what the mechanism is (rule 12). The reader needs to know
                the sentence becomes a visible reply; they do not need to know it becomes a `thoughts`
                row, that a trigger raises a notification, or that the mark is keyed on the thought. */}
            <p className="mt-0.5 text-xs text-neutral-text">
                {localizer("discussion.mind_changed.required")}
            </p>

            <textarea
                ref={textareaRef}
                value={text}
                onChange={(event) => setText(event.target.value)}
                rows={3}
                maxLength={2000}
                aria-label={localizer("discussion.mind_changed.prompt")}
                data-playwright-id="discussion-mind-changed-composer-input"
                // 16px on a coarse pointer: iOS Safari auto-zooms any focused control under 16px.
                className="mt-2 w-full resize-y rounded-lg border border-border/70 bg-input/70 px-3 py-2 text-sm [@media(pointer:coarse)]:text-base focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            />

            <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    // Empty is simply not-yet-ready: the button is inert until there is something to
                    // post. No validation message and no scolding, ever - the note above already says
                    // a sentence is needed, and repeating it as an error would turn the one moment a
                    // member is most willing to write into a form they failed.
                    disabled={!canSubmit}
                    onClick={() => onSubmit(trimmed)}
                    data-playwright-id="discussion-mind-changed-composer-submit"
                    className="inline-flex items-center rounded-full border border-foreground px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                    {localizer("literal.publish")}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={submitting}
                    data-playwright-id="discussion-mind-changed-composer-cancel"
                    className="inline-flex items-center rounded-full border border-border/70 px-4 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                    {localizer("literal.cancel")}
                </button>
            </div>
        </div>
    );
}

export default MindChangedComposer;
